import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ItemReorderEventDetail, ToastController } from '@ionic/angular';
import { IRehearsal } from '../i-rehearsal.interface';
import { RehearsalRequest } from '../rehearsal-request';
import { RehearsalRequestDTO } from '../RehearsalRequestDTO';
import { RehearsalScene } from '../rehearsal-scene';
import { RehearsalSceneItem } from '../rehearsal-scene-item';
import { RehearsalSceneRequestDto } from '../RehearsalSceneRequestDTO';
import { RehearsalSceneItemRequestDTO } from '../RehearsalSceneItemRequestDTO';
import DOMPurify from 'dompurify';

@Component({
    selector: 'app-rehearsal-form',
    templateUrl: './rehearsal-form.component.html',
    styleUrls: ['./rehearsal-form.component.scss'],
    standalone: false
})
export class RehearsalFormComponent implements OnChanges {

    // ==================== INPUTS ====================

    /**
     * All available scenes/items for the show (template data)
     * This is the master list from which selections are made
     */
    @Input() availableScenes: RehearsalScene[] = [];

    /**
     * Initial rehearsal data for edit mode (optional)
     * When provided, form is populated with existing data
     */
    @Input() initialData: IRehearsal | null = null;

    /**
     * Whether the form is in edit mode vs create mode
     * Affects button labels and some UI elements
     */
    @Input() isEditMode: boolean = false;

    /**
     * Whether the form is currently saving (disables inputs)
     */
    @Input() isSaving: boolean = false;

    /**
     * Whether to show scene reordering feature
     */
    @Input() enableSceneReordering: boolean = true;

    /**
     * Whether to show the summary component at the bottom
     */
    @Input() showSummary: boolean = true;

    // ==================== OUTPUTS ====================

    /**
     * Emits when user clicks save button
     * Emits the RehearsalRequestDTO ready for API submission
     */
    @Output() save = new EventEmitter<RehearsalRequestDTO>();

    /**
     * Emits whenever form data changes (for parent tracking)
     * Useful for dirty checking or auto-save features
     */
    @Output() formChange = new EventEmitter<RehearsalRequest>();

    // ==================== INTERNAL STATE ====================

    /** The form model - maintains current form state */
    rehearsalRequest: RehearsalRequest = new RehearsalRequest();

    /** ViewChild reference to the form for validation */
    @ViewChild('rehearsalForm') rehearsalForm: NgForm;

    /** Toast controller for showing validation messages */
    private toastController = inject(ToastController);

    // ==================== LIFECYCLE ====================

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.initialData && this.initialData) {
            this.populateFormFromInitialData();
        }
    }

    // ==================== SCENE/ITEM CHECKBOX LOGIC ====================

    /**
     * Checks if a scene is currently selected
     */
    isSceneRequired(scene: RehearsalScene): boolean {
        return this.rehearsalRequest.scenes.some(s => s.id === scene.id);
    }

    /**
     * Toggles scene selection (used by row click)
     */
    toggleScene(scene: RehearsalScene): void {
        if (this.isSaving) { return; }

        const isCurrentlyRequired = this.isSceneRequired(scene);
        this.setSceneState(scene, !isCurrentlyRequired);
    }

    /**
     * Sets scene selection state (used by checkbox event)
     */
    setSceneRequired(scene: RehearsalScene, $event: any): void {
        const isRequired = $event.detail.checked;
        this.setSceneState(scene, isRequired);
    }

    /**
     * Internal method to set scene state
     */
    private setSceneState(scene: RehearsalScene, isRequired: boolean): void {
        scene.required = isRequired;

        if (isRequired) {
            if (!this.rehearsalRequest.scenes.some(s => s.id === scene.id)) {
                this.rehearsalRequest.scenes.push(scene);
            }
        } else {
            this.rehearsalRequest.scenes = this.rehearsalRequest.scenes.filter(s => s.id !== scene.id);
            // Clear items when scene is unchecked
            if (scene.items) {
                scene.items.forEach(item => item.required = false);
            }
        }
        this.emitFormChange();
    }

    /**
     * Checks if an item is currently selected
     */
    isItemRequired(scene: RehearsalScene, item: RehearsalSceneItem): boolean {
        return item.required === true;
    }

    /**
     * Toggles item selection (used by row click)
     */
    toggleItem(scene: RehearsalScene, item: RehearsalSceneItem): void {
        if (this.isSaving) { return; }

        item.required = !item.required;

        // Auto-add scene to selection when an item is checked
        if (item.required && !this.rehearsalRequest.scenes.some(s => s.id === scene.id)) {
            scene.required = true;
            this.rehearsalRequest.scenes.push(scene);
        }
        this.emitFormChange();
    }

    /**
     * Sets item selection state (used by checkbox event)
     */
    setItemRequired(scene: RehearsalScene, item: RehearsalSceneItem, $event: any): void {
        item.required = $event.detail.checked;

        // Auto-add scene to selection when an item is checked
        if (item.required && !this.rehearsalRequest.scenes.some(s => s.id === scene.id)) {
            scene.required = true;
            this.rehearsalRequest.scenes.push(scene);
        }
        this.emitFormChange();
    }

    /**
     * Returns color for scene item based on selection state
     */
    getColor(scene: RehearsalScene): string {
        return this.isSceneRequired(scene) ? 'success' : 'light';
    }

    // ==================== SCENE REORDERING ====================

    /**
     * Handles drag-and-drop reordering of scenes
     */
    handleSceneReorder(event: CustomEvent<ItemReorderEventDetail>): void {
        if (!event?.detail?.complete) {
            return;
        }
        const reorderedScenes = event.detail.complete(this.rehearsalRequest.scenes);
        if (Array.isArray(reorderedScenes)) {
            this.rehearsalRequest.scenes = reorderedScenes;
            this.rehearsalRequest.scenes.forEach((scene, index) => {
                scene.orderIndex = index;
            });
        }
        this.emitFormChange();
    }

    // ==================== SAVE LOGIC ====================

    /**
     * Checks if the rehearsal has content (at least 1 scene, note, or purpose)
     */
    hasContent(): boolean {
        const hasScenes = this.rehearsalRequest.scenes.length > 0;
        const hasNotes = (this.rehearsalRequest.notes?.trim().length ?? 0) > 0;
        const hasPurpose = (this.rehearsalRequest.purpose?.trim().length ?? 0) > 0;
        return hasScenes || hasNotes || hasPurpose;
    }

    /**
     * Checks if the form is valid for submission
     */
    isFormValid(): boolean {
        return !this.rehearsalForm?.invalid && !this.isSaving && this.hasContent();
    }

    /**
     * Handles save button click - validates form and emits DTO
     */
    async onSaveClick(): Promise<void> {
        if (this.isSaving) {
            return;
        }

        // Mark all fields as touched to show validation errors
        if (this.rehearsalForm?.controls) {
            Object.values(this.rehearsalForm.controls).forEach(control => {
                control.markAsTouched();
            });
        }

        // Check for validation errors
        const validationMessage = this.getValidationMessage();
        if (validationMessage) {
            const toast = await this.toastController.create({
                message: validationMessage,
                duration: 3000,
                color: 'warning',
                position: 'bottom',
                icon: 'alert-circle-outline'
            });
            await toast.present();
            return;
        }

        const dto = this.mapToDTO();
        this.save.emit(dto);
    }

    /**
     * Builds a friendly validation message based on invalid fields
     * Returns null if form is valid
     */
    private getValidationMessage(): string | null {
        // Check required name field
        if (this.rehearsalForm?.controls.personName?.invalid) {
            return 'Vul je naam in om door te gaan.';
        }

        // Check content requirement (at least 1 scene, note, or purpose)
        if (!this.hasContent()) {
            return 'Voeg minimaal een scène, opmerking of doel toe.';
        }

        return null;
    }

    /**
     * Maps internal form state to API DTO
     */
    private mapToDTO(): RehearsalRequestDTO {
        const dto = new RehearsalRequestDTO();
        dto.purpose = this.rehearsalRequest.purpose;
        dto.notes = this.rehearsalRequest.notes;
        dto.personName = this.rehearsalRequest.personName;
        dto.scenes = this.rehearsalRequest.scenes.map((scene, index) => {
            const dtoScene = new RehearsalSceneRequestDto();
            dtoScene.id = scene.id;
            dtoScene.required = true;
            dtoScene.notes = scene.notes || '';
            dtoScene.orderIndex = scene.orderIndex ?? index;
            dtoScene.items = (scene.items || [])
                .filter(item => item.required === true)
                .map(item => {
                    const dtoItem = new RehearsalSceneItemRequestDTO();
                    dtoItem.id = item.id;
                    dtoItem.required = true;
                    return dtoItem;
                });
            return dtoScene;
        });
        return dto;
    }

    // ==================== FORM POPULATION ====================

    /**
     * Populates form from initial data (for edit mode)
     */
    private populateFormFromInitialData(): void {
        this.rehearsalRequest.personName = this.sanitizeInput(this.initialData?.personName || '');
        this.rehearsalRequest.purpose = this.sanitizeInput(this.initialData?.purpose || '');
        this.rehearsalRequest.notes = this.sanitizeInput(this.initialData?.notes || '');
        // Copy performance for summary display (needs datetime)
        if (this.initialData?.performance) {
            this.rehearsalRequest.performance = this.initialData.performance as any;
        }
        this.rehearsalRequest.scenes = [];

        if (this.initialData?.scenes && this.initialData.scenes.length > 0) {
            this.initialData.scenes.forEach(rehearsalScene => {
                const templateScene = this.availableScenes.find(s => s.id === rehearsalScene.id);
                if (templateScene) {
                    templateScene.required = true;
                    templateScene.notes = rehearsalScene.notes || '';
                    templateScene.orderIndex = rehearsalScene.orderIndex;

                    if (rehearsalScene.items && templateScene.items) {
                        // First reset all items
                        templateScene.items.forEach(item => item.required = false);
                        // Then mark the ones from the rehearsal as required
                        rehearsalScene.items.forEach(reqItem => {
                            const templateItem = templateScene.items.find(i => i.id === reqItem.id);
                            if (templateItem) {
                                templateItem.required = reqItem.required;
                            }
                        });
                    }
                    this.rehearsalRequest.scenes.push(templateScene);
                }
            });

            // Sort scenes by orderIndex
            this.rehearsalRequest.scenes.sort((a, b) => {
                if (a.orderIndex == null && b.orderIndex == null) { return 0; }
                if (a.orderIndex == null) { return 1; }
                if (b.orderIndex == null) { return -1; }
                return a.orderIndex - b.orderIndex;
            });
        }
    }

    /**
     * Sanitizes user input to prevent XSS attacks using DOMPurify.
     * Returns plain text with all HTML tags stripped.
     */
    sanitizeInput(input: string): string {
        if (!input) {
            return '';
        }
        // Use DOMPurify with ALLOWED_TAGS: [] to strip all HTML and return plain text
        return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    }

    /**
     * Emits form change event
     */
    private emitFormChange(): void {
        this.formChange.emit(this.rehearsalRequest);
    }
}
